/**
 * Pure presentation mapping for the unioned environment picker (forge#3520).
 *
 * Answers "what does this ONE row look like right now" — dot variant, status
 * label, kind label — with no React imports, mirroring the role
 * `cloudRuntimeRowState.logic.ts` plays for the sidebar. Status colors go
 * through the same duty-cycled token classes the sidebar dot uses; the labels
 * are picker-local because the three kinds do not share one status vocabulary.
 */

import type {
  CmmdEnvironmentPickerEntry,
  CmmdEnvironmentKind,
} from "../../environments/cmmdEnvironments/cmmdEnvironmentListTypes";

export type EnvironmentDotVariant = "green" | "amber-pulse" | "grey" | "red-pulse" | "hollow";

/** Same token classes as the sidebar runtime dot, so a cloud row reads
 * identically in both lists. `animate-status-pulse`, never `animate-pulse`. */
const ENVIRONMENT_DOT_CLASS: Readonly<Record<EnvironmentDotVariant, string>> = {
  green: "bg-success",
  "amber-pulse": "bg-warning motion-safe:animate-status-pulse",
  grey: "bg-muted-foreground/40",
  "red-pulse": "bg-destructive motion-safe:animate-status-pulse",
  hollow: "border border-muted-foreground/40 bg-transparent",
};

/** The cloud statuses CMMD publishes on the picker row (`forgeRuntimeStatuses`
 * minus the retired ones the server drops). */
const CLOUD_STATUS_VARIANTS: Readonly<Record<string, EnvironmentDotVariant>> = {
  running: "green",
  provisioning: "amber-pulse",
  starting: "amber-pulse",
  suspended: "grey",
  stopped: "grey",
  recycling: "amber-pulse",
  draining: "amber-pulse",
  error: "red-pulse",
};

const CLOUD_STATUS_LABELS: Readonly<Record<string, string>> = {
  provisioning: "Provisioning",
  starting: "Starting",
  running: "Running",
  suspended: "Suspended",
  stopped: "Suspended",
  recycling: "Recycling",
  draining: "Updating",
  error: "Error",
};

/** Registry presence statuses for desktop and local machines. */
const MACHINE_STATUS_VARIANTS: Readonly<Record<string, EnvironmentDotVariant>> = {
  online: "green",
  suspect: "amber-pulse",
  offline: "grey",
};

const MACHINE_STATUS_LABELS: Readonly<Record<string, string>> = {
  online: "Online",
  suspect: "Reaching...",
  offline: "Offline",
};

const KIND_LABELS: Readonly<Record<CmmdEnvironmentKind, string>> = {
  "cloud-sprite": "Cloud sprite",
  desktop: "Desktop",
  local: "Local machine",
};

export function environmentKindLabel(kind: CmmdEnvironmentKind): string {
  return KIND_LABELS[kind];
}

export function environmentDotVariant(entry: CmmdEnvironmentPickerEntry): EnvironmentDotVariant {
  const status = entry.status ?? "";
  if (entry.kind === "cloud-sprite") {
    return CLOUD_STATUS_VARIANTS[status] ?? "hollow";
  }
  return MACHINE_STATUS_VARIANTS[status] ?? "grey";
}

export function environmentDotClass(entry: CmmdEnvironmentPickerEntry): string {
  return ENVIRONMENT_DOT_CLASS[environmentDotVariant(entry)];
}

export function environmentStatusLabel(entry: CmmdEnvironmentPickerEntry): string {
  const status = entry.status ?? "";
  if (entry.kind === "cloud-sprite") {
    return CLOUD_STATUS_LABELS[status] ?? "Unknown";
  }
  return MACHINE_STATUS_LABELS[status] ?? "Unknown";
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * The row's last-seen line ("2m ago"), or null when the payload carried no
 * timestamp. `now` is injectable so the mapping stays a pure function; the
 * picker passes Date.now(). Anything unparseable renders as nothing rather
 * than as a wrong duration.
 */
export function formatRelativeLastSeen(lastSeenAt: string | null, now: number): string | null {
  if (lastSeenAt === null) return null;
  const seenAt = Date.parse(lastSeenAt);
  if (!Number.isFinite(seenAt)) return null;
  const elapsed = Math.max(0, now - seenAt);
  if (elapsed < MINUTE_MS) return "just now";
  if (elapsed < HOUR_MS) return `${Math.floor(elapsed / MINUTE_MS)}m ago`;
  if (elapsed < DAY_MS) return `${Math.floor(elapsed / HOUR_MS)}h ago`;
  if (elapsed < 30 * DAY_MS) return `${Math.floor(elapsed / DAY_MS)}d ago`;
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(
    new Date(seenAt),
  );
}
